/**
 * PartialDateInput
 * Accepts partial genealogical dates: year only, year+month, or full date.
 * Stores as YYYY, YYYY-MM, or YYYY-MM-DD string. A calendar button opens a
 * native date picker for when the exact day is known (fills all three fields).
 */
import { useRef } from 'react'

const MONTHS = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

/**
 * Normalize a numeric part to exactly 2 characters, '01'..'99'. Collapses
 * leading zeros beyond two chars ('026' → '26') and rejects non-numeric
 * input cleanly. padStart alone only *pads*, never truncates — which is
 * how a stray '026' in the day field could persist indefinitely.
 */
function normalizePart(s) {
  if (!s) return ''
  const n = parseInt(s, 10)
  if (!Number.isFinite(n) || n <= 0) return ''
  return String(n).padStart(2, '0')
}

/**
 * Parse a stored date string into { year, month, day } parts.
 * Month/day stay zero-padded so they compare equal to the <option value>s
 * the select renders ('01'..'12' / '01'..'31'). Stripping the padding was
 * a past bug — the select never found a match and kept falling back to the
 * 'Month' placeholder.
 */
function parse(value) {
  if (!value) return { year: '', month: '', day: '' }
  const parts = value.split('-')
  return {
    year:  parts[0] || '',
    month: normalizePart(parts[1]),
    day:   normalizePart(parts[2]),
  }
}

/**
 * Combine parts back into a canonical string.
 * Returns '' if no year, 'YYYY' if year only, 'YYYY-MM' if year+month,
 * 'YYYY-MM-DD' if all three.
 */
function format({ year, month, day }) {
  if (!year) return ''
  const m = normalizePart(month)
  if (!m) return year
  const d = normalizePart(day)
  if (!d) return `${year}-${m}`
  return `${year}-${m}-${d}`
}

export default function PartialDateInput({ value, onChange, id }) {
  const parts = parse(value)
  const nativeRef = useRef(null)
  const fullDate = parts.year && parts.month && parts.day ? value : ''

  function openCalendar() {
    const el = nativeRef.current
    if (!el) return
    if (typeof el.showPicker === 'function') { try { el.showPicker() } catch { el.focus() } }
    else el.focus()
  }

  function update(field, val) {
    const next = { ...parts, [field]: val }
    // If month is cleared, also clear day
    if (field === 'month' && !val) next.day = ''
    onChange(format(next))
  }

  return (
    <div className="partial-date">
      <input
        id={id}
        type="number"
        className="partial-date__year"
        placeholder="YYYY"
        min="1"
        max="2100"
        value={parts.year}
        onChange={e => update('year', e.target.value)}
      />
      <select
        className="partial-date__month"
        value={parts.month}
        onChange={e => update('month', e.target.value)}
        disabled={!parts.year}
      >
        <option value="">Month</option>
        {MONTHS.slice(1).map((m, i) => (
          <option key={m} value={String(i + 1).padStart(2, '0')}>{m}</option>
        ))}
      </select>
      <select
        className="partial-date__day"
        value={parts.day}
        onChange={e => update('day', e.target.value)}
        disabled={!parts.month}
        title={!parts.month ? 'Pick a month first' : 'Day'}
      >
        <option value="">Day</option>
        {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0')).map(d => (
          <option key={d} value={d}>{parseInt(d, 10)}</option>
        ))}
      </select>
      <button
        type="button"
        className="partial-date__cal"
        onClick={openCalendar}
        title="Pick an exact date from a calendar"
        aria-label="Pick from calendar"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
        </svg>
        <input
          ref={nativeRef}
          type="date"
          className="partial-date__native"
          tabIndex={-1}
          value={fullDate}
          onChange={e => e.target.value && onChange(e.target.value)}
        />
      </button>
    </div>
  )
}

/**
 * Format a stored date string for display.
 * "1887" => "1887"
 * "1887-03" => "March 1887"
 * "1887-03-15" => "15 March 1887"
 */
export function formatDateDisplay(value) {
  if (!value) return ''
  const parts = value.split('-')
  const year = parts[0]
  const month = parts[1] ? parseInt(parts[1], 10) : null
  const day = parts[2] ? parseInt(parts[2], 10) : null

  if (!year) return ''
  if (!month) return year
  if (!day) return `${MONTHS[month]} ${year}`
  return `${day} ${MONTHS[month]} ${year}`
}
